/**
 * Browser reader for the inky-agent event stream (ported from Cairn Flint lib/ai/chat-stream.ts).
 *
 * The server sends `text/event-stream` frames whose `data:` lines carry one JSON event
 * (CONTRACTS-W2 §C): turn_start, text_delta, text_reset, tool_start, tool_end, client_action,
 * done, error. `: keep-alive` comment frames carry no data and are skipped.
 *
 * Text deltas are coalesced to ONE update per animation frame (many tokens per frame become one
 * registry update), while every other event flushes pending text first so the order of text and
 * tool events is preserved exactly. `text_reset` drops pending text before it is shown.
 */

/** What {@link readInkyStream} reports when the stream ended without `done` or `error`. */
export const STREAM_DROPPED = "STREAM_DROPPED";

/**
 * Schedule a callback on the next animation frame (setTimeout fallback outside browsers).
 * @param {() => void} callback - Callback.
 * @returns {{cancel: () => void}} Handle.
 */
const frameScheduler = (callback) => {
  if (typeof requestAnimationFrame === "function") {
    const id = requestAnimationFrame(callback);
    return { cancel: () => cancelAnimationFrame(id) };
  }
  const id = setTimeout(callback, 16);
  return { cancel: () => clearTimeout(id) };
};

/**
 * Parse one SSE frame (lines up to a blank line) into an event.
 * @param {string} frame - Raw frame.
 * @returns {object|null} Event, or null for comments / malformed frames.
 */
export const parseSseFrame = (frame) => {
  const data = String(frame || "")
    .split(/\r?\n/)
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).replace(/^ /, ""))
    .join("\n");
  if (!data) return null;
  try {
    const event = JSON.parse(data);
    return event && typeof event === "object" && typeof event.type === "string" ? event : null;
  } catch {
    return null;
  }
};

/**
 * Consume the inky-agent stream.
 * @param {ReadableStream<Uint8Array>} body - Response body.
 * @param {(event: object) => void} onEvent - Receives every non-terminal event (text coalesced per frame).
 * @returns {Promise<object>} The terminal `done` / `error` event, or `{type: 'error', code: STREAM_DROPPED}`.
 */
export const readInkyStream = async (body, onEvent) => {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let pendingText = "";
  let scheduled = null;

  const flushText = () => {
    scheduled?.cancel();
    scheduled = null;
    if (!pendingText) return;
    const text = pendingText;
    pendingText = "";
    onEvent({ type: "text_delta", text });
  };

  const queueText = (text) => {
    if (!text) return;
    pendingText += text;
    if (scheduled) return;
    scheduled = frameScheduler(() => {
      scheduled = null;
      flushText();
    });
  };

  /**
   * Route one event; returns it when it ends the stream.
   * @param {object} event - Parsed event.
   * @returns {object|null} Terminal event.
   */
  const accept = (event) => {
    if (event.type === "text_delta") {
      queueText(typeof event.text === "string" ? event.text : "");
      return null;
    }
    if (event.type === "text_reset") {
      scheduled?.cancel();
      scheduled = null;
      pendingText = "";
      onEvent(event);
      return null;
    }
    flushText();
    if (event.type === "done" || event.type === "error") return event;
    onEvent(event);
    return null;
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      for (;;) {
        const match = /\r?\n\r?\n/.exec(buffer);
        if (!match) break;
        const frame = buffer.slice(0, match.index);
        buffer = buffer.slice(match.index + match[0].length);
        const event = parseSseFrame(frame);
        if (!event) continue;
        const terminal = accept(event);
        if (terminal) return terminal;
      }
    }
    buffer += decoder.decode();
    if (buffer.trim()) {
      const event = parseSseFrame(buffer);
      if (event) {
        const terminal = accept(event);
        if (terminal) return terminal;
      }
    }
    flushText();
  } finally {
    scheduled?.cancel();
    try {
      reader.releaseLock();
    } catch {
      // The reader is already released (aborted request).
    }
  }
  return { type: "error", code: STREAM_DROPPED, message: "The connection dropped mid-answer." };
};

/**
 * Honest message for a gateway failure, checked from the HTTP status BEFORE the body is read
 * (an edge 502 carries its own JSON body that is not ours).
 * @param {number} status - HTTP status.
 * @returns {string|null} Message, or null when the status is not a gateway failure.
 */
export const gatewayFailure = (status) => {
  if (status < 500) return null;
  if (status === 502 || status === 503) return "Inky is restarting. Nothing was lost; send that again in a moment.";
  if (status === 504) return "That request timed out before Inky finished. Ask me to continue and I'll pick up from there.";
  return "The server couldn't complete that request. Try again.";
};
